import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { CreateIrregularity, IrregularitiesList, IrregularityInfo } from 'features/irregularities';
import { AlertMessage, TestDayWarningModal } from 'shared/components';
import * as irregularitiesActions from 'features/irregularities/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as roomActions from 'features/room-builder/duck';
import { PermissionsService } from 'shared/services';
import { isEventDate } from 'shared/helpers';

import './IrregularitiesDashboard.scss';

const findPersonnelRole = (personnel, id) => {
  const user = personnel.find((elem) => elem.testCenterCycleId === id);

  return user?.role ? user.role.role : '';
};

const canApprove = (irreg, tcEvent) =>
  !irreg.reviewCompleteFlag &&
  (PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId) ||
    PermissionsService.hasEventManage(tcEvent.id));

class IrregularitiesDashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      irregularity: {},
      modalOpen: false,
      userRole: findPersonnelRole(
        props.user.testCenterCyclePersonnels,
        props.tcEvent.testCenterCycleId
      ),
      warningTestDayOpen: false,
      isTestDay:
        props.tcEvent &&
        props.serverDateTime &&
        isEventDate(props.tcEvent.testingEvent, props.serverDateTime),
    };

    this.showIrregularityDetails = this.showIrregularityDetails.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onSubmitReviewed = this.onSubmitReviewed.bind(this);
    this.onChange = this.onChange.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.onCreate = this.onCreate.bind(this);
    this.closeWarningTestDay = this.closeWarningTestDay.bind(this);
  }

  componentDidMount() {
    if (this.props.roomId) {
      this.props.actions.getRoom(this.props.roomId);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.irregularity !== this.props.irregularity) {
      this.setState({
        irregularity: this.props.irregularity,
      });
    }

    if (!isEmpty(this.props.tcEvent) && !isEmpty(this.props.serverDateTime)) {
      if (
        prevProps.tcEvent !== this.props.tcEvent ||
        prevProps.serverDateTime !== this.props.serverDateTime
      ) {
        const { testingEvent } = this.props.tcEvent;
        const { serverDateTime } = this.props;
        this.setState({
          isTestDay: isEventDate(testingEvent, serverDateTime),
        });
      }
    }
  }

  onSubmitReviewed() {
    const { irregularity } = this.state;
    const { roomId, tcEvent } = this.props;

    if (canApprove(irregularity, tcEvent)) {
      irregularity.reviewCompleteFlag = true;

      const reviewPersonnel = this.props.user;
      const { testCenterCyclePersonnels, testCenterEventPersonnels, ...updatedPersonnel } =
        reviewPersonnel;
      irregularity.reviewPersonnel = updatedPersonnel;
      this.props.actions.updateIrregularity(irregularity).then(() => {
        if (roomId) {
          this.props.actions.getRoomIrregularities(roomId);
        } else {
          this.props.actions.getTcEventIrregularities(tcEvent.id);
        }
        this.setState({
          showMessage: true,
          showMessageTime: 3000,
        });
      });
    }
    this.closeModal();
  }

  onChange(e) {
    const { value } = e.target;

    this.setState((prevState) => ({
      irregularity: {
        ...prevState.irregularity,
        reviewNotes: value,
      },
    }));
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  closeModal() {
    this.setState({
      modalOpen: false,
    });
    this.props.actions.getIrregularityAction({ data: {}, loading: false });
  }

  showIrregularityDetails(id) {
    this.props.actions.getIrregularity(id).then(() => {
      this.setState({
        modalOpen: true,
      });
    });
  }

  onCreate() {
    if (!this.state.isTestDay && !this.state.warningTestDayOpen) {
      this.setState({
        warningTestDayOpen: true,
      });
      return;
    }
    this.props.onCreate();
  }

  closeWarningTestDay() {
    this.setState({
      warningTestDayOpen: false,
    });
  }

  render() {
    const {
      irregularities,
      irregularityCategories,
      irregularityStages,
      tcEvent,
      startPosition,
      tcCycle,
    } = this.props;
    const { userRole } = this.state;

    return (
      <div className="irregularities-dashboard">
        <CreateIrregularity onCreate={this.onCreate} />
        {!!irregularityCategories.length && (
          <IrregularitiesList
            role={userRole}
            showInfo={this.showIrregularityDetails}
            irregularities={irregularities}
            startPosition={startPosition}
            setStartPosition={this.props.setStartPosition}
            categories={irregularityCategories}
          />
        )}
        {!isEmpty(this.state.irregularity) && (
          <IrregularityInfo
            role={userRole}
            tcEvent={tcEvent}
            hideMatchIdFlag={tcCycle.testCenterProgram.testCenter.nowManagedFlag}
            irregularity={this.state.irregularity}
            allRoomsDTO={this.props.allRoomsDTO}
            irregularityStages={irregularityStages}
            modalOpen={this.state.modalOpen}
            onChange={this.onChange}
            closeModal={this.closeModal}
            onSubmitReviewed={this.onSubmitReviewed}
          />
        )}
        <button className="btn btn-outline ml-0 w-100 mt-3" onClick={this.props.onBack}>
          {this.props.t('common.labels.back')}
        </button>
        <AlertMessage
          show={this.state.showMessage}
          time={this.state.showMessageTime}
          removeMessage={this.removeMessage}
          message="irregularities.alertReviewedMessage"
        />
        <TestDayWarningModal
          warningModalOpen={this.state.warningTestDayOpen}
          onSubmit={this.props.onCreate}
          closeWarningModal={this.closeWarningTestDay}
          message="irregularities.testDayWarning"
          startLabel="irregularities.logIrregularityLabel"
        />
      </div>
    );
  }
}

IrregularitiesDashboard.propTypes = {
  irregularity: PropTypes.shape({
    reviewCompleteFlag: PropTypes.bool,
  }).isRequired,
  user: PropTypes.shape({
    testCenterCyclePersonnels: PropTypes.arrayOf(
      PropTypes.shape({
        testCenterCycleId: PropTypes.number,
        role: PropTypes.shape({
          role: PropTypes.string,
        }),
      })
    ),
    testCenterEventPersonnels: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  irregularities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  irregularityCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  irregularityStages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tcEvent: PropTypes.shape({
    id: PropTypes.number,
    testCenterCycleId: PropTypes.number,
    testingEvent: PropTypes.shape({
      name: PropTypes.string,
      testDates: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  serverDateTime: PropTypes.string,
  tcCycle: PropTypes.shape({
    testCenterProgram: PropTypes.shape({
      testCenter: PropTypes.shape({
        nowManagedFlag: PropTypes.bool,
      }),
    }),
  }).isRequired,
  allRoomsDTO: PropTypes.arrayOf(PropTypes.shape({})),
  startPosition: PropTypes.number,
  roomId: PropTypes.number,
  actions: PropTypes.shape({
    setIrregularity: PropTypes.func.isRequired,
    getIrregularity: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    updateIrregularity: PropTypes.func.isRequired,
    getRoomIrregularities: PropTypes.func.isRequired,
    getTcEventIrregularities: PropTypes.func.isRequired,
    getIrregularityAction: PropTypes.func.isRequired,
  }).isRequired,
  onCreate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  setStartPosition: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    user: state.users.currentUser,
    irregularity: state.irregularities.currentIrregularity || {},
    serverDateTime: state.statics.serverDateTime,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { ...irregularitiesActions, ...staticActions, ...roomActions },
      dispatch
    ),
  };
}

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(IrregularitiesDashboard)
);
